/**
 * Accounting React Query Hooks
 *
 * Provides hooks for managing accounts, movements, invoices, and chart of accounts.
 * Replaces the server-data portion of accountingStore with React Query patterns.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';

// =============================================================================
// TYPES (imported from domain, not re-exported to avoid barrel conflicts)
// =============================================================================

import type {
  Account,
  Movement,
  Invoice,
  InvoiceItem,
  InvoiceStatus,
  InvoiceDirection,
  Direction,
  CentsAmount,
} from '../domain/models';

import type {
  ChartOfAccountsNode,
  AccountCategory,
} from '../domain/accounting';

// =============================================================================
// DEFAULT CHART OF ACCOUNTS
// =============================================================================

export const DEFAULT_CHART_OF_ACCOUNTS: ChartOfAccountsNode[] = [
  // Revenue
  { id: 'coa-4000', parentId: null, code: '4000', name: 'Ricavi da vendite', category: 'revenue', isLeaf: false, order: 1, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-4010', parentId: 'coa-4000', code: '4010', name: 'Servizi professionali', category: 'revenue', isLeaf: true, order: 1, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-4020', parentId: 'coa-4000', code: '4020', name: 'Consulenze', category: 'revenue', isLeaf: true, order: 2, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-4030', parentId: 'coa-4000', code: '4030', name: 'Vendita prodotti', category: 'revenue', isLeaf: true, order: 3, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },

  // Expenses
  { id: 'coa-6000', parentId: null, code: '6000', name: 'Costi operativi', category: 'expenses', isLeaf: false, order: 2, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6010', parentId: 'coa-6000', code: '6010', name: 'Affitto e utenze', category: 'expenses', isLeaf: true, order: 1, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6020', parentId: 'coa-6000', code: '6020', name: 'Software e abbonamenti', category: 'expenses', isLeaf: true, order: 2, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6030', parentId: 'coa-6000', code: '6030', name: 'Hosting e domini', category: 'expenses', isLeaf: true, order: 3, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6040', parentId: 'coa-6000', code: '6040', name: 'Marketing e pubblicità', category: 'expenses', isLeaf: true, order: 4, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6050', parentId: 'coa-6000', code: '6050', name: 'Spese bancarie', category: 'expenses', isLeaf: true, order: 5, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-6060', parentId: 'coa-6000', code: '6060', name: 'Forniture ufficio', category: 'expenses', isLeaf: true, order: 6, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },

  // Assets
  { id: 'coa-1000', parentId: null, code: '1000', name: 'Attività', category: 'assets', isLeaf: false, order: 3, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-1010', parentId: 'coa-1000', code: '1010', name: 'Conti bancari', category: 'assets', isLeaf: true, order: 1, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-1020', parentId: 'coa-1000', code: '1020', name: 'Crediti clienti', category: 'assets', isLeaf: true, order: 2, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },

  // Liabilities
  { id: 'coa-2000', parentId: null, code: '2000', name: 'Passività', category: 'liabilities', isLeaf: false, order: 4, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-2010', parentId: 'coa-2000', code: '2010', name: 'Debiti fornitori', category: 'liabilities', isLeaf: true, order: 1, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'coa-2020', parentId: 'coa-2000', code: '2020', name: 'Imposte da versare', category: 'liabilities', isLeaf: true, order: 2, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
];

// =============================================================================
// GRAPHQL QUERIES
// =============================================================================

const GET_BANK_ACCOUNTS_QUERY = `
  query GetBankAccounts {
    bankAccounts {
      id
      name
      currency
      iban
      balance
    }
  }
`;

const GET_INVOICES_QUERY = `
  query GetInvoices {
    invoices {
      id
      number
      date
      dueDate
      direction
      customerName
      status
      items {
        description
        quantity
        unitPrice
        vatRate
        total
      }
      total
      vatTotal
      projectId
      notes
      createdAt
      updatedAt
    }
  }
`;

const GET_MOVEMENTS_QUERY = `
  query GetMovements {
    movements {
      id
      accountId
      at
      amount
      direction
      counterparty
      memo
      reconciled
      invoiceId
      categoryId
    }
  }
`;

const GET_CHART_OF_ACCOUNTS_QUERY = `
  query GetChartOfAccounts {
    chartOfAccounts {
      id
      parentId
      code
      name
      category
      isLeaf
      order
      active
      createdAt
      updatedAt
    }
  }
`;

// =============================================================================
// GRAPHQL MUTATIONS
// =============================================================================

const CREATE_ACCOUNT_MUTATION = `
  mutation CreateAccount($input: AccountInput!) {
    createAccount(input: $input) {
      id
      name
      currency
      iban
      balance
    }
  }
`;

const UPDATE_ACCOUNT_MUTATION = `
  mutation UpdateAccount($id: ID!, $input: AccountInput!) {
    updateAccount(id: $id, input: $input) {
      id
      name
      currency
      iban
      balance
    }
  }
`;

const DELETE_ACCOUNT_MUTATION = `
  mutation DeleteAccount($id: ID!) {
    deleteAccount(id: $id)
  }
`;

const CREATE_MOVEMENT_MUTATION = `
  mutation CreateMovement($input: MovementInput!) {
    createMovement(input: $input) {
      id
      accountId
      at
      amount
      direction
      counterparty
      memo
      reconciled
      invoiceId
      categoryId
    }
  }
`;

const UPDATE_MOVEMENT_MUTATION = `
  mutation UpdateMovement($id: ID!, $input: MovementInput!) {
    updateMovement(id: $id, input: $input) {
      id
      accountId
      at
      amount
      direction
      counterparty
      memo
      reconciled
      invoiceId
      categoryId
    }
  }
`;

const DELETE_MOVEMENT_MUTATION = `
  mutation DeleteMovement($id: ID!) {
    deleteMovement(id: $id)
  }
`;

const RECONCILE_MOVEMENT_MUTATION = `
  mutation ReconcileMovement($id: ID!, $invoiceId: ID) {
    reconcileMovement(id: $id, invoiceId: $invoiceId) {
      id
      reconciled
      invoiceId
    }
  }
`;

const CREATE_INVOICE_MUTATION = `
  mutation CreateInvoice($input: InvoiceInput!) {
    createInvoice(input: $input) {
      id
      number
      date
      dueDate
      direction
      customerName
      status
      items {
        description
        quantity
        unitPrice
        vatRate
        total
      }
      total
      vatTotal
      projectId
      notes
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_INVOICE_MUTATION = `
  mutation UpdateInvoice($id: ID!, $input: InvoiceInput!) {
    updateInvoice(id: $id, input: $input) {
      id
      number
      date
      dueDate
      direction
      customerName
      status
      items {
        description
        quantity
        unitPrice
        vatRate
        total
      }
      total
      vatTotal
      projectId
      notes
      updatedAt
    }
  }
`;

const DELETE_INVOICE_MUTATION = `
  mutation DeleteInvoice($id: ID!) {
    deleteInvoice(id: $id)
  }
`;

const UPDATE_INVOICE_STATUS_MUTATION = `
  mutation UpdateInvoiceStatus($id: ID!, $status: String!) {
    updateInvoiceStatus(id: $id, status: $status) {
      id
      status
      updatedAt
    }
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

/**
 * Map GraphQL bank account to local Account type.
 * Converts balance from decimal to cents.
 */
function mapGraphQLAccount(data: any): Account {
  return {
    id: data.id,
    name: data.name,
    currency: data.currency,
    iban: data.iban,
    balance: Math.round(data.balance * 100),
  };
}

/**
 * Map GraphQL invoice to local Invoice type.
 * Converts totals from decimal to cents.
 */
function mapGraphQLInvoice(data: any): Invoice {
  return {
    id: data.id,
    number: data.number,
    at: data.date,
    dueAt: data.dueDate,
    direction: data.direction as InvoiceDirection,
    counterparty: data.customerName,
    status: data.status as InvoiceStatus,
    items: Array.isArray(data.items) ? data.items : [],
    total: Math.round(data.total * 100),
    vatTotal: Math.round(data.vatTotal * 100),
    projectId: data.projectId,
    notes: data.notes,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

/**
 * Map GraphQL movement to local Movement type.
 */
function mapGraphQLMovement(data: any): Movement {
  return {
    id: data.id,
    accountId: data.accountId,
    at: data.at,
    amount: data.amount,
    direction: data.direction as Direction,
    counterparty: data.counterparty,
    memo: data.memo,
    reconciled: data.reconciled || false,
    invoiceId: data.invoiceId,
  };
}

/**
 * Map GraphQL chart of accounts node to local type.
 */
function mapGraphQLChartNode(data: any): ChartOfAccountsNode {
  return {
    id: data.id,
    parentId: data.parentId,
    code: data.code,
    name: data.name,
    category: data.category as AccountCategory,
    isLeaf: data.isLeaf,
    order: data.order,
    active: data.active,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all bank accounts from the server.
 */
export function useAccounts() {
  return useQuery({
    queryKey: queryKeys.accounting.bankAccounts(),
    queryFn: async () => {
      const response = await graphqlFetch<{ bankAccounts: any[] }>(GET_BANK_ACCOUNTS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.bankAccounts || []).map(mapGraphQLAccount);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch all movements from the server.
 */
export function useMovements() {
  return useQuery({
    queryKey: [...queryKeys.accounting.all(), 'movements'],
    queryFn: async () => {
      const response = await graphqlFetch<{ movements: any[] }>(GET_MOVEMENTS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.movements || []).map(mapGraphQLMovement);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch all invoices from the server.
 */
export function useInvoices() {
  return useQuery({
    queryKey: queryKeys.accounting.invoices(),
    queryFn: async () => {
      const response = await graphqlFetch<{ invoices: any[] }>(GET_INVOICES_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.invoices || []).map(mapGraphQLInvoice);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch chart of accounts from the server.
 */
export function useChartOfAccounts() {
  return useQuery({
    queryKey: [...queryKeys.accounting.all(), 'chartOfAccounts'],
    queryFn: async () => {
      const response = await graphqlFetch<{ chartOfAccounts: any[] }>(GET_CHART_OF_ACCOUNTS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return DEFAULT_CHART_OF_ACCOUNTS;
      }
      const nodes = (response.data?.chartOfAccounts || []).map(mapGraphQLChartNode);
      return nodes.length > 0 ? nodes : DEFAULT_CHART_OF_ACCOUNTS;
    },
    staleTime: 30 * 60_000, // 30 minutes
  });
}

// =============================================================================
// MUTATION HOOKS - ACCOUNTS
// =============================================================================

/**
 * Create a new account.
 */
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (account: {
      name: string;
      currency: string;
      iban?: string;
      balance?: number;
    }) => {
      const input = {
        name: account.name,
        currency: account.currency,
        iban: account.iban || null,
        balance: account.balance ? account.balance / 100 : 0, // Convert cents to decimal
      };
      const response = await graphqlFetch<{ createAccount: any }>(
        CREATE_ACCOUNT_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createAccount) {
        return mapGraphQLAccount(response.data.createAccount);
      }
      throw new Error('Failed to create account');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.bankAccounts() });
    },
  });
}

/**
 * Update an existing account.
 */
export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Account>;
    }) => {
      const input = {
        name: updates.name || null,
        currency: updates.currency || null,
        iban: updates.iban || null,
        balance: updates.balance ? updates.balance / 100 : null, // Convert cents to decimal
      };
      const response = await graphqlFetch<{ updateAccount: any }>(
        UPDATE_ACCOUNT_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateAccount) {
        return mapGraphQLAccount(response.data.updateAccount);
      }
      throw new Error('Failed to update account');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.bankAccounts() });
    },
  });
}

/**
 * Delete an account.
 */
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteAccount: boolean }>(
        DELETE_ACCOUNT_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteAccount) {
        throw new Error('Failed to delete account');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.bankAccounts() });
      qc.invalidateQueries({ queryKey: [...queryKeys.accounting.all(), 'movements'] });
    },
  });
}

// =============================================================================
// MUTATION HOOKS - MOVEMENTS
// =============================================================================

/**
 * Create a new movement.
 */
export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (movement: {
      accountId: string;
      at: string;
      amount: number;
      direction: Direction;
      counterparty: string;
      memo?: string;
      reconciled?: boolean;
      invoiceId?: string;
      categoryId?: string;
    }) => {
      const input = {
        account_id: movement.accountId,
        at: movement.at,
        amount: movement.amount,
        direction: movement.direction,
        counterparty: movement.counterparty,
        memo: movement.memo || null,
        reconciled: movement.reconciled || false,
        invoice_id: movement.invoiceId || null,
        category_id: movement.categoryId || null,
      };
      const response = await graphqlFetch<{ createMovement: any }>(
        CREATE_MOVEMENT_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createMovement) {
        return mapGraphQLMovement(response.data.createMovement);
      }
      throw new Error('Failed to create movement');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.accounting.all(), 'movements'] });
    },
  });
}

/**
 * Update an existing movement.
 */
export function useUpdateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Movement>;
    }) => {
      const input = {
        account_id: updates.accountId || null,
        at: updates.at || null,
        amount: updates.amount || null,
        direction: updates.direction || null,
        counterparty: updates.counterparty || null,
        memo: updates.memo || null,
        reconciled: updates.reconciled !== undefined ? updates.reconciled : null,
        invoice_id: updates.invoiceId || null,
      };
      const response = await graphqlFetch<{ updateMovement: any }>(
        UPDATE_MOVEMENT_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateMovement) {
        return mapGraphQLMovement(response.data.updateMovement);
      }
      throw new Error('Failed to update movement');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.accounting.all(), 'movements'] });
    },
  });
}

/**
 * Delete a movement.
 */
export function useDeleteMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteMovement: boolean }>(
        DELETE_MOVEMENT_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteMovement) {
        throw new Error('Failed to delete movement');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.accounting.all(), 'movements'] });
    },
  });
}

/**
 * Reconcile a movement with an optional invoice.
 */
export function useReconcileMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      invoiceId,
    }: {
      id: string;
      invoiceId?: string;
    }) => {
      const response = await graphqlFetch<{ reconcileMovement: any }>(
        RECONCILE_MOVEMENT_MUTATION,
        { id, invoiceId: invoiceId || null }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.reconcileMovement) {
        return mapGraphQLMovement(response.data.reconcileMovement);
      }
      throw new Error('Failed to reconcile movement');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.accounting.all(), 'movements'] });
    },
  });
}

// =============================================================================
// MUTATION HOOKS - INVOICES
// =============================================================================

/**
 * Create a new invoice.
 */
export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoice: {
      number: string;
      at: string;
      dueAt?: string;
      direction: InvoiceDirection;
      counterparty: string;
      status: InvoiceStatus;
      items: InvoiceItem[];
      projectId?: string;
      notes?: string;
    }) => {
      const { total, vatTotal } = calculateInvoiceTotal(invoice.items);
      const input = {
        number: invoice.number,
        date: invoice.at,
        due_date: invoice.dueAt || null,
        direction: invoice.direction,
        customer_name: invoice.counterparty,
        status: invoice.status,
        items: invoice.items,
        total: total / 100, // Convert cents to decimal
        vat_total: vatTotal / 100,
        project_id: invoice.projectId || null,
        notes: invoice.notes || null,
      };
      const response = await graphqlFetch<{ createInvoice: any }>(
        CREATE_INVOICE_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createInvoice) {
        return mapGraphQLInvoice(response.data.createInvoice);
      }
      throw new Error('Failed to create invoice');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.invoices() });
    },
  });
}

/**
 * Update an existing invoice.
 */
export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Invoice>;
    }) => {
      let total = updates.total;
      let vatTotal = updates.vatTotal;

      // Recalculate totals if items changed
      if (updates.items) {
        const calculated = calculateInvoiceTotal(updates.items);
        total = calculated.total;
        vatTotal = calculated.vatTotal;
      }

      const input = {
        number: updates.number || null,
        date: updates.at || null,
        due_date: updates.dueAt || null,
        direction: updates.direction || null,
        customer_name: updates.counterparty || null,
        status: updates.status || null,
        items: updates.items || null,
        total: total ? total / 100 : null, // Convert cents to decimal
        vat_total: vatTotal ? vatTotal / 100 : null,
        project_id: updates.projectId || null,
        notes: updates.notes || null,
      };
      const response = await graphqlFetch<{ updateInvoice: any }>(
        UPDATE_INVOICE_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateInvoice) {
        return mapGraphQLInvoice(response.data.updateInvoice);
      }
      throw new Error('Failed to update invoice');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.invoices() });
    },
  });
}

/**
 * Delete an invoice.
 */
export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteInvoice: boolean }>(
        DELETE_INVOICE_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteInvoice) {
        throw new Error('Failed to delete invoice');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.invoices() });
    },
  });
}

/**
 * Update an invoice status.
 */
export function useUpdateInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: InvoiceStatus;
    }) => {
      const response = await graphqlFetch<{ updateInvoiceStatus: any }>(
        UPDATE_INVOICE_STATUS_MUTATION,
        { id, status }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateInvoiceStatus) {
        return mapGraphQLInvoice(response.data.updateInvoiceStatus);
      }
      throw new Error('Failed to update invoice status');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.accounting.invoices() });
    },
  });
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// formatEUR and calculateInvoiceTotal are exported from accountingStore.ts
// to avoid barrel conflicts. Import from '@business-assistant/shared' directly.

/**
 * Filter movements by direction or reconciliation status.
 */
export function filterMovements(movements: Movement[], filterKey: string): Movement[] {
  switch (filterKey) {
    case 'in':
      return movements.filter((m) => m.direction === 'in');
    case 'out':
      return movements.filter((m) => m.direction === 'out');
    case 'unreconciled':
      return movements.filter((m) => !m.reconciled);
    default:
      return movements;
  }
}

/**
 * Filter invoices by direction or status.
 */
export function filterInvoices(invoices: Invoice[], filterKey: string): Invoice[] {
  switch (filterKey) {
    case 'invoices-issued':
      return invoices.filter((i) => i.direction === 'issued');
    case 'invoices-received':
      return invoices.filter((i) => i.direction === 'received');
    case 'invoices-overdue':
      return invoices.filter((i) => i.status === 'overdue');
    case 'invoices-paid':
      return invoices.filter((i) => i.status === 'paid');
    default:
      return invoices;
  }
}

/**
 * Calculate total balance from all movements.
 */
export function getTotalBalance(movements: Movement[]): CentsAmount {
  return movements.reduce((sum, m) => {
    return m.direction === 'in' ? sum + m.amount : sum - m.amount;
  }, 0);
}

/**
 * Get balance for a specific account.
 */
export function getAccountBalance(movements: Movement[], accountId: string): CentsAmount {
  return movements
    .filter((m) => m.accountId === accountId)
    .reduce((sum, m) => {
      return m.direction === 'in' ? sum + m.amount : sum - m.amount;
    }, 0);
}

/**
 * Get movements for a specific account, sorted by date (newest first).
 */
export function getAccountMovements(movements: Movement[], accountId: string): Movement[] {
  return movements
    .filter((m) => m.accountId === accountId)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

/**
 * Get overdue invoices (past due date and not paid/cancelled).
 */
export function getOverdueInvoices(invoices: Invoice[]): Invoice[] {
  const now = new Date();
  return invoices.filter((i) => {
    if (i.status === 'paid' || i.status === 'cancelled') return false;
    if (!i.dueAt) return false;
    return new Date(i.dueAt) < now;
  });
}

/**
 * Get total of pending issued invoices (sent or overdue).
 */
export function getPendingInvoicesTotal(invoices: Invoice[]): CentsAmount {
  return invoices
    .filter((i) => i.direction === 'issued' && (i.status === 'sent' || i.status === 'overdue'))
    .reduce((sum, i) => sum + i.total, 0);
}

/**
 * Get category by ID from chart of accounts.
 */
export function getCategoryById(chart: ChartOfAccountsNode[], id: string): ChartOfAccountsNode | null {
  return chart.find((node) => node.id === id) || null;
}

/**
 * Get categories by type (revenue, expenses, etc.).
 */
export function getCategoriesByType(chart: ChartOfAccountsNode[], category: AccountCategory): ChartOfAccountsNode[] {
  return chart.filter((node) => node.category === category);
}

/**
 * Get only leaf categories (can have transactions).
 */
export function getLeafCategories(chart: ChartOfAccountsNode[]): ChartOfAccountsNode[] {
  return chart.filter((node) => node.isLeaf);
}

// =============================================================================
// NON-REACT CACHE ACCESS
// =============================================================================

/**
 * Get accounts from React Query cache (non-hook access).
 */
export function getAccountsFromCache(): Account[] {
  return queryClient.getQueryData<Account[]>(queryKeys.accounting.bankAccounts()) || [];
}

/**
 * Get invoices from React Query cache (non-hook access).
 */
export function getInvoicesFromCache(): Invoice[] {
  return queryClient.getQueryData<Invoice[]>(queryKeys.accounting.invoices()) || [];
}

/**
 * Get movements from React Query cache (non-hook access).
 */
export function getMovementsFromCache(): Movement[] {
  return queryClient.getQueryData<Movement[]>([...queryKeys.accounting.all(), 'movements']) || [];
}
